import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import VideoWaterfall from '@/components/feature/VideoWaterfall';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getCategoryByKey, getVideosByCategory } from '@/utils/video';
import type { Video } from '@/types';

const sortOptions = [
  { key: 'latest', label: '最新' },
  { key: 'hot', label: '最热' },
];

const durationOptions = [
  { key: 'all', label: '全部时长' },
  { key: 'lt1', label: '1 分钟以内' },
  { key: '1to3', label: '1-3 分钟' },
  { key: 'gt3', label: '3 分钟以上' },
];

const tagOptions = [
  { key: 'all', label: '全部' },
  { key: '原创', label: '原创' },
  { key: '精选', label: '精选' },
  { key: '热门', label: '热门' },
];

const PAGE_SIZE = 8;

function durationToSeconds(d: string): number {
  const [m, s] = d.split(':').map(Number);
  return m * 60 + s;
}

export default function CategoryPage() {
  const { type = 'music' } = useParams();
  const category = getCategoryByKey(type);

  const [sort, setSort] = useState('latest');
  const [duration, setDuration] = useState('all');
  const [tag, setTag] = useState('all');
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);

  usePageMeta({
    title: category ? `${category.name} - 抖音短视频频道` : '分类 - 抖音短视频频道',
    description: category
      ? `${category.name}频道：${category.desc} 在抖音发现更多${category.name}类精彩短视频内容。`
      : '抖音短视频频道，发现音乐、搞笑、生活、美食等精彩内容。',
    keywords: category ? `${category.name},抖音${category.name},短视频分类,抖音短视频` : '抖音分类,短视频分类',
  });

  const filtered = useMemo(() => {
    let list: Video[] = getVideosByCategory(type);

    if (duration !== 'all') {
      list = list.filter((v) => {
        const sec = durationToSeconds(v.duration);
        if (duration === 'lt1') return sec < 60;
        if (duration === '1to3') return sec >= 60 && sec <= 180;
        return sec > 180;
      });
    }

    if (tag !== 'all') {
      list = list.filter((v) => v.authorTag === tag);
    }

    if (sort === 'hot') {
      list = [...list].sort((a, b) => b.plays - a.plays);
    } else {
      list = [...list].sort((a, b) => (a.publishDate < b.publishDate ? 1 : -1));
    }
    return list;
  }, [type, sort, duration, tag]);

  const visible = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = visible.length < filtered.length;

  const resetFilters = () => {
    setSort('latest');
    setDuration('all');
    setTag('all');
    setPage(1);
  };

  const applyFilter = (fn: () => void) => {
    fn();
    setPage(1);
  };

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Header />
        <main className="flex-1 flex flex-col items-center justify-center text-center px-4 pt-24">
          <span className="w-16 h-16 rounded-full bg-secondary-100 text-secondary-400 flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">没有找到这个分类</h1>
          <p className="text-sm text-foreground-500 mb-6">该分类可能已下线，去看看其他精彩内容吧</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold cursor-pointer"
          >
            <i className="ri-home-5-line"></i>
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />
      <main className="flex-1 pt-16">
        {/* 分类头部 */}
        <section className="relative w-full">
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={category.cover}
              alt={`${category.name}分类 - 抖音短视频`}
              title={`${category.name}分类 - 抖音短视频`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/70 to-foreground-950/40"></div>
          </div>
          <div className="relative w-full px-4 md:px-8 py-10 md:py-16">
            {/* 面包屑 */}
            <nav className="flex items-center gap-2 text-xs text-background-100/80 mb-5" aria-label="面包屑">
              <Link to="/" className="hover:text-background-50 cursor-pointer flex items-center gap-1">
                <i className="ri-home-4-line"></i>
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-background-50">{category.name}</span>
            </nav>
            <div className="flex items-center gap-4">
              <span className="w-14 h-14 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center shrink-0">
                <i className={`${category.icon} text-3xl`}></i>
              </span>
              <div>
                <h1 className="text-2xl md:text-4xl font-black text-background-50">{category.name}</h1>
                <p className="text-background-100/85 text-sm mt-1.5 max-w-2xl">{category.desc}</p>
              </div>
            </div>
          </div>
        </section>

        {/* 筛选栏 */}
        <section className="sticky top-16 z-30 bg-background-50/95 backdrop-blur border-b border-background-200/70">
          <div className="w-full px-4 md:px-8 py-3">
            {/* Desktop */}
            <div className="hidden md:flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">排序</span>
                <div className="flex items-center gap-1 p-1 rounded-full bg-secondary-100">
                  {sortOptions.map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => applyFilter(() => setSort(o.key))}
                      className={`px-3.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                        sort === o.key ? 'bg-background-50 text-primary-600' : 'text-secondary-800 hover:text-foreground-900'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">时长</span>
                <div className="flex items-center gap-1 flex-wrap">
                  {durationOptions.map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => applyFilter(() => setDuration(o.key))}
                      className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                        duration === o.key
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-secondary-100 text-secondary-800 hover:bg-secondary-200'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">标签</span>
                <div className="flex items-center gap-1">
                  {tagOptions.map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => applyFilter(() => setTag(o.key))}
                      className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                        tag === o.key
                          ? 'bg-accent-500 text-background-50'
                          : 'bg-secondary-100 text-secondary-800 hover:bg-secondary-200'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1" />
              <span className="text-xs text-foreground-500">共 {filtered.length} 个视频</span>
            </div>

            {/* Mobile */}
            <div className="md:hidden flex items-center justify-between gap-3">
              <div className="flex items-center gap-1 p-1 rounded-full bg-secondary-100">
                {sortOptions.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => applyFilter(() => setSort(o.key))}
                    className={`px-3.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      sort === o.key ? 'bg-background-50 text-primary-600' : 'text-secondary-800'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setPanelOpen((v) => !v)}
                className="flex items-center gap-1.5 h-8 px-3 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium cursor-pointer"
              >
                <i className="ri-filter-3-line"></i>
                筛选
              </button>
            </div>

            {panelOpen && (
              <div className="md:hidden mt-3 pt-3 border-t border-background-200/70 space-y-3">
                <div>
                  <p className="text-xs text-foreground-500 mb-2">时长</p>
                  <div className="flex flex-wrap gap-2">
                    {durationOptions.map((o) => (
                      <button
                        key={o.key}
                        type="button"
                        onClick={() => applyFilter(() => setDuration(o.key))}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                          duration === o.key ? 'bg-primary-500 text-background-50' : 'bg-secondary-100 text-secondary-800'
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-2">标签</p>
                  <div className="flex flex-wrap gap-2">
                    {tagOptions.map((o) => (
                      <button
                        key={o.key}
                        type="button"
                        onClick={() => applyFilter(() => setTag(o.key))}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                          tag === o.key ? 'bg-accent-500 text-background-50' : 'bg-secondary-100 text-secondary-800'
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* 内容 */}
        <section className="w-full px-4 md:px-8 py-8">
          {filtered.length === 0 ? (
            <div className="w-full py-20 flex flex-col items-center justify-center text-center">
              <span className="w-16 h-16 rounded-full bg-secondary-100 text-secondary-400 flex items-center justify-center mb-4">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h2 className="text-lg font-bold text-foreground-950 mb-2">没有符合条件的视频</h2>
              <p className="text-sm text-foreground-500 mb-6">试着调整一下筛选条件，或重置后再看看</p>
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-2 h-10 px-6 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          ) : (
            <>
              <VideoWaterfall videos={visible} />

              {/* 分页 / 加载更多 */}
              <div className="mt-8 flex flex-col items-center gap-4">
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    className="inline-flex items-center gap-2 h-11 px-8 rounded-full border border-primary-300 text-primary-600 hover:bg-primary-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                    加载更多
                  </button>
                ) : (
                  <p className="text-xs text-foreground-400">已经到底啦，看看其他分类吧</p>
                )}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                      page <= 1 ? 'bg-secondary-100 text-secondary-300' : 'bg-secondary-100 text-secondary-800 hover:bg-secondary-200 cursor-pointer'
                    }`}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  <span className="text-xs text-foreground-500 font-label px-2">
                    第 {page} / {Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))} 页
                  </span>
                  <button
                    type="button"
                    disabled={!hasMore}
                    onClick={() => setPage((p) => p + 1)}
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                      !hasMore ? 'bg-secondary-100 text-secondary-300' : 'bg-secondary-100 text-secondary-800 hover:bg-secondary-200 cursor-pointer'
                    }`}
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}